import { html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_DETAILS } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  GeneralDetailsFormValue,
  GeneralDetailsSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.types';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import {
  getGeneralDetailsDefaultValue,
  getGeneralDetailsSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import _ from 'lodash';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { InputWidthValues } from '../../../../../../../ui/gzInput';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { formatDate } from '../../../../../../../endpointInvestigation/activityGrid/view/customGridRenderers';
import { RegexValidator } from '../../../../../../../ui/gzForm/validators/regex.validator';
import { MaxLengthValidator } from '../../../../../../../ui/gzForm/validators/max-length.validator';
import { RequiredValidator } from 'apps/webcomponents/src/ui/gzForm/validators/required.validator';
import { RemoteValidator } from '../../../../../../../ui/gzForm/validators/remote.validator';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { store } from '@nirvana/redux/store';
import { setIsWaitingValidation } from '../../../../redux/saveButtonSlice/actions.save';
import {
  PolicyDependenciesContainer,
  PolicyTemplatesApiClients,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { SpinnerWidth } from 'apps/webcomponents/src/ui/gzProgressSpinner/types';
import { toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { selectPoliciesState } from '../../../../redux/slice';
import { getPhantom } from '../../../../redux/actions';
import { CustomizableValidator } from '../../../../../../../ui/gzForm/validators/customizable.validator';
import { classMap } from 'lit/directives/class-map.js';
import { DataLoadedStatus, PolicyAppState } from '../../../../redux/types';
import { getIsGeneralDetailsValidationLoaded } from '../../../../redux/customStates/general/general.actions';
import { EventKeyManager } from '../../../../../../../utils/keys';
import { distinctUntilChanged } from 'rxjs/operators';

const MAX_LENGTH_POLICY_NAME = 80;
const REMOTE_VALIDATION_DELAY = 1000;
const SPINNER_WIDTH_VALUE = 24;

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_DETAILS)
export class GzGeneralDetailsWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  GeneralDetailsFormValue,
  GeneralDetailsSearchableKeywords,
  PolicyTemplatesApiClients
> {
  protected hasPendingValidationSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected hasPendingValidation$: Observable<boolean> = this.hasPendingValidationSubject.pipe(distinctUntilChanged());

  protected path = {
    moduleId: ModuleId.POLICY_SETTINGS,
    sectionId: SectionId.DETAILS,
  };

  protected searchableKeywords = getGeneralDetailsSearchableKeywords();

  protected POLICY_NAME_VALIDITY_PATH = ['remoteValidationHistory', 'general', 'details'];

  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  @query('#name-form-field')
  private nameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#allow-change-by-other-users-form-field')
  private allowChangeByOtherUsersFormField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#created-by-form-field')
  private createdByFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#created-on-form-field')
  private createdOnFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#modified-on-form-field')
  private modifiedOnFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#website-form-field')
  private websiteFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#email-form-field')
  private emailFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#phone-form-field')
  private phoneFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#name-spinner')
  private nameSpinner: HTMLElement;

  private validationCounter = 0;
  private validationResult: ValidationResult = { valid: true };

  protected createFormGroup(): void {
    this.formGroup = FormGroup.build({
      name: this.nameFormField,
      lastModified: this.modifiedOnFormField,
      ownerFullName: this.createdByFormField,
      created: this.createdOnFormField,
      uiSettings: {
        general: {
          allowChangeByOtherUsers: this.allowChangeByOtherUsersFormField,
          display: {
            supportInformation: {
              website: this.websiteFormField,
              email: this.emailFormField,
              phone: this.phoneFormField,
            },
          },
        },
      },
    });
  }

  protected setClients() {
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency(
      'PolicyTemplates'
    ) as PolicyTemplatesApiClients;
  }

  private isPolicyInUpdate() {
    const policyState = selectPoliciesState(store.getState());
    return !getPhantom(policyState);
  }

  protected setValidators() {
    this.nameFormField.Validators = [CustomizableValidator(this.customNameValidation.bind(this))];
    this.websiteFormField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.emailFormField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.phoneFormField.Validators = [RequiredValidator(translateFn('Required field'))];

    // subject should be changed only after the name validation is finished
    this.nameFormField.valid$.subscribe(valid => {
      this.hasPendingValidationSubject.next(false);
    });
  }
  private async customNameValidation(value: string) {
    // setup all validators
    const nameValidators = [
      RequiredValidator(translateFn('Policy name is required.')),
      RegexValidator(/^[^\s]+/i, translateFn('Invalid policy name')),
      MaxLengthValidator(
        MAX_LENGTH_POLICY_NAME,
        translateFn('The policy name must not exceed {maxLength} characters.', {
          maxLength: MAX_LENGTH_POLICY_NAME,
        })
      ),
    ];
    // increment the request counter to keep track of the current request
    const currentRequestCounter = this.validationCounter + 1;
    this.validationCounter = currentRequestCounter;

    let nameValidationResponse: ValidationResult = { valid: true };

    // validate the value with all validators
    for (const validator of nameValidators) {
      const validationResult = await validator(value);
      if (!validationResult.valid) {
        nameValidationResponse = validationResult;
        break;
      }
    }

    // loading state is set to false in case any validator fails
    // this is done to avoid the spinner to be displayed when the field is empty
    // the result of the required validator is returned and kept in the validationResult variable
    if (!nameValidationResponse.valid) {
      this.setLoadingValidationState(false);

      this.validationResult = nameValidationResponse;
      return this.validationResult;
    }

    // if the policy is in update mode and the default policy is loaded then the remote validation is skipped
    if (!(this.isPolicyInUpdate() && PolicyPanelManager.getInstance().getPolicy('default'))) {
      await this.updateComplete;
      const additionalInfo = PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
        this.POLICY_NAME_VALIDITY_PATH,
        null
      );
      if (additionalInfo && additionalInfo.value === value) {
        //validation cases
        if (additionalInfo.isPolicyValid) {
          this.validationResult = { valid: true };
          this.setLoadingValidationState(false);
          return this.validationResult;
        }
        if (additionalInfo.isPolicyValid === undefined) {
          this.validationResult = {
            valid: false,
            error: translateFn('A communication error has occurred. Please try again later.'),
          };
          this.setLoadingValidationState(false);
          return this.validationResult;
        }
        this.validationResult = { valid: false, error: translateFn('Policy name is invalid or already exists.') };
        this.setLoadingValidationState(false);
        return this.validationResult;
      }
      this.hasPendingValidationSubject.next(true);
      // performing remote validation
      this.setLoadingValidationState(true);
      const remoteValidationResponse = await RemoteValidator(
        (val: string) => of(this.remotePolicyNameValidation(val, currentRequestCounter)),
        (response: ValidationResult) => {
          return response.valid;
        },
        (response: { valid: false; error: string }) => {
          return response.error;
        },
        translateFn(translateFn('A communication error has occurred. Please try again later.')),
        REMOTE_VALIDATION_DELAY
      )(value);

      // remote validation is done so the loading state is set to false
      this.setLoadingValidationState(false);
      // validationResult is updated with the result of the remote validation
      this.validationResult = remoteValidationResponse;
    }
    return this.validationResult;
  }

  private setLoadingValidationState(state: boolean) {
    store.dispatch(setIsWaitingValidation(state));
    toggleVisibility(this.nameSpinner, state);
  }

  private async getPolicyValidity(value: string): Promise<boolean> {
    // make request to check if the policy name is valid
    // policyId should be taken into account only on add
    const policyId = this.isPolicyInUpdate() ? PolicyPanelManager.getInstance().getPolicyId() : null;
    const policyNameValidity = (await this.clients.isPolicyValid(value, policyId)).success;

    // validation should be sent even though
    PolicyPanelManager.getInstance().setAdditionalPolicyInfoToPath(this.POLICY_NAME_VALIDITY_PATH, {
      value: value,
      isPolicyValid: policyNameValidity ?? false,
    });
    return policyNameValidity ?? false;
  }

  private async remotePolicyNameValidation(value: string, currentRequestCounter: number): Promise<ValidationResult> {
    try {
      const policyValidity = await this.getPolicyValidity(value);
      // if the validationCounter has changed, it means that a new validation has been made and this one is no longer valid
      if (this.validationCounter > currentRequestCounter) {
        return this.validationResult;
      }

      if (!policyValidity) {
        return { valid: false, error: translateFn('Policy name is invalid or already exists.') };
      }
      return { valid: true };
    } catch (e) {
      console.error(e);
      return {
        valid: false,
        error: translateFn('A communication error has occurred. Please try again later.'),
      };
    }
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, []) as FormGroupValue;
    }
    if (this.isPolicyInUpdate()) {
      _.set(value, ['lastModified'], formatDate(new Date()));
    } else {
      _.set(value, ['lastModified'], null);
    }

    const createdValue = _.get(value, ['created'], 'N/A');
    if (createdValue === 'N/A') {
      _.set(value, ['created'], '');
    }

    return value;
  }

  protected getValueFromManager(): GeneralDetailsFormValue {
    const defaultValue = getGeneralDetailsDefaultValue();

    // get default value
    const policyManagerInstance = PolicyPanelManager.getInstance();

    // get value from manager
    const value = {
      name: policyManagerInstance.getPolicy('name'),
      lastModified: policyManagerInstance.getPolicy('lastModified'),
      ownerFullName: policyManagerInstance.getPolicy('ownerFullName'),
      created: policyManagerInstance.getPolicy('created'),
      uiSettings: {
        [ModuleId.GENERAL]: {
          allowChangeByOtherUsers: policyManagerInstance.getPolicy('uiSettings.general.allowChangeByOtherUsers', false),
          [SectionId.GENERAL_DISPLAY]: {
            supportInformation: policyManagerInstance.getPolicy('uiSettings.general.display.supportInformation'),
          },
        },
      },
    };
    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  private handleNameChange(event: CustomEvent) {
    const policyName = event.detail.sourceEvent;
    PolicyPanelManager.getInstance().setPolicyNameForNavigation(policyName);
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <span>
      <gz-title
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.PolicyDetails].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.TITLE}"
      >
        ${this.searchableKeywords[GeneralDetailsSearchableKeywords.PolicyDetails].translatedLabel}
      </gz-title></span
    >`;
  }

  /**
   * Render specific info text.
   */
  protected renderInfoText() {
    return html``;
  }

  protected async onKeyDown(event: KeyboardEvent) {
    if (!EventKeyManager.isPrintable(event)) {
      return;
    }
    const additionalInfo = PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
      this.POLICY_NAME_VALIDITY_PATH,
      null
    );
    if (additionalInfo && additionalInfo.value !== this.nameFormField.value) {
      this.setLoadingValidationState(true);
    }
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html` <div id="general-details-content" class="mt-2">
      <gz-form-field
        class="label-mw-250"
        id="name-form-field"
        label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.Name].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.Name].label}"
        @change="${this.handleNameChange}"
        required
      >
        <div id="policyName">
          <gz-input gz-form-input @keydown="${this.onKeyDown}" inputWidth="${InputWidthValues.LARGE}"></gz-input>
          <gz-progress-spinner
            id="name-spinner"
            spinnerWidth="${SpinnerWidth.CUSTOM}"
            customSpinnerWidthValue="${SPINNER_WIDTH_VALUE}"
            class="hidden"
          ></gz-progress-spinner>
        </div>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field
        class="mt-2 ${classMap({
          hidden: this.isHiddenAllowChangeByOtherUsers(),
        })}"
        id="allow-change-by-other-users-form-field"
      >
        <gz-checkbox
          gz-form-input
          fontWeight="${FontWeights.NORMAL}"
          fontSize="${FontSizes.PARAGRAPH_11}"
          label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.AllowChangeByOtherUsers].translatedLabel}"
          searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.AllowChangeByOtherUsers].label}"
        ></gz-checkbox>
      </gz-form-field>
      <gz-title
        class="form-subtitle mt-4"
        id="history-title"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.History].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.HEADING_4}"
      >
        ${this.searchableKeywords[GeneralDetailsSearchableKeywords.History].translatedLabel}
      </gz-title>
      <gz-form-field
        class="mt-2"
        id="created-by-form-field"
        label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.CreatedBy].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.CreatedBy].label}"
        readonly
      >
        <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
      </gz-form-field>
      <gz-form-field
        class="label-mw-250 form-field-margin"
        id="created-on-form-field"
        label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.CreatedOn].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.CreatedOn].label}"
        readonly
      >
        <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
      </gz-form-field>
      <gz-form-field
        class="label-mw-250 form-field-margin"
        id="modified-on-form-field"
        label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.ModifiedOn].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.ModifiedOn].label}"
        readonly
      >
        <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
      </gz-form-field>
      <gz-title
        class="form-subtitle"
        id="support-information-title"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.TechnicalSupportInformation]
          .label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.HEADING_4}"
      >
        ${this.searchableKeywords[GeneralDetailsSearchableKeywords.TechnicalSupportInformation].translatedLabel}
      </gz-title>
      <gz-text
        id="technical-support-info-text"
        fontSize="${FontSizes.PARAGRAPH_12}"
        fontWeight="400"
        class="form-field-info-text mt-1"
        >${translateFn("The Technical Support information is displayed in the agent's About window.")}
      </gz-text>
      <gz-form-field
        class="mt-2"
        id="website-form-field"
        label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.Website].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.Website].label}"
        required
      >
        <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
      </gz-form-field>
      <gz-form-field
        class="label-mw-250 form-field-margin"
        id="email-form-field"
        label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.Email].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.Email].label}"
        required
      >
        <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
      </gz-form-field>
      <gz-form-field
        class="label-mw-250 form-field-margin"
        id="phone-form-field"
        label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.Phone].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralDetailsSearchableKeywords.Phone].label}"
        required
      >
        <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
      </gz-form-field>
    </div>`;
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }
    this.formGroup.value = {
      name: this.valueT.name,
      lastModified: this.valueT.lastModified ? formatDate(this.valueT.lastModified) : 'N/A',
      ownerFullName: this.valueT.ownerFullName,
      created: this.valueT.created ? formatDate(this.valueT.created) : 'N/A',
      uiSettings: {
        general: {
          allowChangeByOtherUsers: this.valueT.uiSettings.general.allowChangeByOtherUsers,
          display: {
            supportInformation: this.valueT.uiSettings.general.display.supportInformation,
          },
        },
      },
    };
  }

  protected isHiddenAllowChangeByOtherUsers() {
    if (this.isPolicyInUpdate()) {
      if (PolicyPanelManager.getInstance().getPolicy('default')) {
        return true;
      } else {
        return PolicyPanelManager.getInstance().getPolicy('ownerId') !== Application.Main.user._id;
      }
    } else {
      return false;
    }
  }

  protected getAdditionalInfoFromManager(): any {
    return PolicyPanelManager.getInstance().getAdditionalPolicyInfo(this.POLICY_NAME_VALIDITY_PATH, null);
  }

  protected isAdditionalInfoLoaded(policiesApp: PolicyAppState): boolean {
    return getIsGeneralDetailsValidationLoaded(policiesApp) !== DataLoadedStatus.NOT_LOADED;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_GENERAL_DETAILS: GzGeneralDetailsWebComponent;
  }
}
